import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from './firebase';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import LoginScreen from './components/LoginScreen';
import LobbyScreen from './components/LobbyScreen';
import GameScreen from './components/GameScreen';
import { useSound } from './hooks/useSound';

// YENİ İMPORTLAR: React Router Rotaları ve Yasal Sayfalar
import { Routes, Route } from 'react-router-dom';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';

// Mobil cihaz kontrolü
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const isMobileUA = mobileRegex.test(userAgent.toLowerCase());
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  return isMobileUA || (isTouchDevice && isSmallScreen);
};

// Mobil Uyarı Ekranı Komponenti
const MobileWarningScreen = () => {
  return (
    <div className="min-h-screen w-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .fade-in-scale {
          animation: fadeInScale 0.5s ease-out;
        }

        .glass-panel {
          background: rgba(15, 23, 42, 0.4);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(148, 163, 184, 0.1);
        }

        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(239, 68, 68, 0.3);
          }
          50% {
            box-shadow: 0 0 40px rgba(239, 68, 68, 0.5);
          }
        }

        .desktop-icon {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        .logo-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .logo-image {
          max-width: 480px;
          width: 100%;
          height: auto;
          filter: drop-shadow(0 0 20px rgba(59, 130, 246, 0.3));
        }
      `}</style>

      <div className="w-full max-w-md fade-in-scale">

        <div className="glass-panel rounded-2xl p-8 text-center">
          {/* İkon */}
          <div className="mb-0 flex justify-center">
            <div className="text-center mb-0">
              <div className="logo-container">
                <img
                  src="/logo-transparent.png"
                  alt="Axio War Logo"
                  className="logo-image"
                />
              </div>
            </div>

          </div>
          <p className="text-slate-400 text-lg">Stratejik Savaş Oyunu</p>

          {/* Mesaj */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">
              Mobil Cihaz Desteklenmiyor
            </h2>

            <div className="space-y-3 text-slate-300">
              <p className="text-sm leading-relaxed">
                AXIO WAR, karmaşık oyun mekaniği ve geniş oyun alanı nedeniyle
                <span className="text-blue-400 font-semibold"> masaüstü veya laptop bilgisayarlar</span> için
                optimize edilmiştir.
              </p>

              <div className="pt-4 pb-2 border-t border-slate-700">
                <p className="text-xs text-slate-400">
                  En iyi oyun deneyimi için lütfen bir bilgisayardan erişin.
                </p>
              </div>
            </div>

            {/* Platform İkonları */}
            <div className="pt-4 flex justify-center gap-8 text-5xl opacity-40">
              <span title="Windows" className="transition-opacity hover:opacity-70">🪟</span>
              <span title="MacOS" className="transition-opacity hover:opacity-70">🍎</span>
              <span title="Linux" className="transition-opacity hover:opacity-70">🐧</span>
            </div>
          </div>
        </div>

        {/* Alt Bilgi */}
        <div className="mt-6 text-center">
          <div className="glass-panel rounded-xl p-4">
            <p className="text-xs text-slate-400 leading-relaxed">
              💡 <span className="font-semibold">İpucu:</span> Tarayıcınızın masaüstü modunu deneyebilirsiniz,
              ancak performans sorunları yaşayabilirsiniz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('login');
  const [currentRoom, setCurrentRoom] = useState(null);
  const [isRestoring, setIsRestoring] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [isMobile] = useState(isMobileDevice());
  const { isMuted, toggleMute, preloadSounds, playSound } = useSound();
  const musicRef = useRef(null);

  // Auth state takibi
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const restoreGameState = async () => {
      if (!authChecked) {
        return;
      }

      try {
        const savedRoomCode = localStorage.getItem('axiati_room_code');
        const savedScreen = localStorage.getItem('axiati_screen');

        if (savedRoomCode && savedScreen && currentUser) {
          const roomRef = doc(db, 'rooms', savedRoomCode);
          const roomSnap = await getDoc(roomRef);

          if (roomSnap.exists()) {
            const roomData = roomSnap.data();

            const isPlayerInRoom = roomData.players.some(
              p => p.id === currentUser.uid
            );

            if (isPlayerInRoom) {
              setCurrentRoom(savedRoomCode);
              setCurrentScreen(savedScreen);

              setTimeout(() => {
                const notification = document.createElement('div');
                notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse';
                notification.textContent = '✓ Oyun kaldığın yerden devam ediyor!';
                document.body.appendChild(notification);

                setTimeout(() => {
                  notification.remove();
                }, 3000);
              }, 500);
            } else {
              localStorage.removeItem('axiati_room_code');
              localStorage.removeItem('axiati_screen');
            }
          } else {
            localStorage.removeItem('axiati_room_code');
            localStorage.removeItem('axiati_screen');
          }
        } else {
        }
      } catch (error) {
        console.error('❌ Geri yükleme hatası:', error);
        localStorage.removeItem('axiati_room_code');
        localStorage.removeItem('axiati_screen');
      } finally {
        setIsRestoring(false);
      }
    };

    if (authChecked) {
      if (currentUser) {
        restoreGameState();
      } else {
        setIsRestoring(false);
        setCurrentScreen('login');
      }
    }
  }, [currentUser, authChecked]);

  useEffect(() => {
    if (currentRoom && (currentScreen === 'lobby' || currentScreen === 'game')) {
      localStorage.setItem('axiati_room_code', currentRoom);
      localStorage.setItem('axiati_screen', currentScreen);
    }
  }, [currentRoom, currentScreen]);

  const handleGameStart = useCallback(() => {
    setCurrentScreen('game');
  }, []);

  const handleJoinLobby = useCallback((roomCode) => {
    setCurrentRoom(roomCode);

    const savedScreen = localStorage.getItem('axiati_screen');
    if (savedScreen === 'game') {
      setCurrentScreen('game');
    } else {
      setCurrentScreen('lobby');
    }
  }, []);

  useEffect(() => {
    preloadSounds([
      'loading',
      'click',
      'gameplay',
      'attack',
      'success',
      'failure',
      'dice-roll',
      'expand',
      'critical-hit',
      'game-over'
    ]);

    const shouldPlayMusic = currentUser && (currentScreen === 'login' || currentScreen === 'lobby');

    if (shouldPlayMusic) {
      if (!musicRef.current) {
        const audio = new Audio('/sounds/loading.mp3');
        audio.loop = true;
        audio.volume = 0.3;
        musicRef.current = audio;

        musicRef.current.muted = isMuted;

        audio.play().catch(err => {
          console.warn("Müzik çalınamadı (etkileşim bekleniyor olabilir):", err);
        });
      }
    } else {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.currentTime = 0;
        musicRef.current = null;
      }
    }

    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current = null;
      }
    };
  }, [currentUser, currentScreen, preloadSounds, isMuted]);

  useEffect(() => {
    if (musicRef.current) {
      musicRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const handleLeaveLobby = useCallback(() => {
    localStorage.removeItem('axiati_room_code');
    localStorage.removeItem('axiati_screen');
    setCurrentRoom(null);
    setCurrentScreen('login');
  }, []);

  const handleGameEnd = useCallback(() => {
    localStorage.removeItem('axiati_room_code');
    localStorage.removeItem('axiati_screen');
    setCurrentRoom(null);
    setCurrentScreen('login');
  }, []);

  useEffect(() => {
    if (!currentUser && authChecked) {
      localStorage.removeItem('axiati_room_code');
      localStorage.removeItem('axiati_screen');
      setCurrentRoom(null);
      setCurrentScreen('login');
    }
  }, [currentUser, authChecked]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (currentScreen === 'game' || currentScreen === 'lobby') {
        e.preventDefault();
        e.returnValue = 'Oyun devam ediyor. Çıkmak istediğinize emin misiniz?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentScreen]);

  // Mobil kontrolü - TÜM HOOK'LARDAN SONRA
  if (isMobile) {
    return <MobileWarningScreen />;
  }

  if (isRestoring || !authChecked) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <style>{`
          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          .fade-in-scale {
            animation: fadeInScale 0.5s ease-out;
          }

          @keyframes pulse-slow {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
            }
          }

          .loading-logo {
            animation: pulse-slow 2s ease-in-out infinite;
          }

          .loading-logo-image {
            max-width: 280px;
            width: 100%;
            height: auto;
            filter: drop-shadow(0 0 30px rgba(59, 130, 246, 0.4));
          }
        `}</style>

        <div className="text-center fade-in-scale">
          <div className="mb-6 loading-logo">
            <img
              src="/logo-transparent.png"
              alt="Axio War Logo"
              className="loading-logo-image mx-auto"
            />
          </div>

          <div className="space-y-3">
            <div className="text-white text-xl font-semibold">Oyun Yükleniyor</div>
            <div className="text-slate-400 text-sm">Lütfen bekleyin...</div>

            <div className="flex justify-center gap-1 mt-6">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // BURASI ARTIK TEK BİR ROTADAN OLUŞAN BİR ROUTES BLOKUDUR
  return (
    <div className="App bg-blue-900 min-h-screen text-white">
      <Routes>
        {/* YASAL SAYFALAR: Router context'ini App'ten aldığı için artık Link hatası vermez */}
        <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
        <Route path="/kullanim-sartlari" element={<TermsOfService />} />

        {/* ANA UYGULAMA ROTASI: Oyun akışını barındırır */}
        <Route path="/" element={
          <>
            {currentScreen === 'login' && (
              <LoginScreen
                currentUser={currentUser}
                onJoinLobby={handleJoinLobby}
                isMuted={isMuted}
                toggleMute={toggleMute}
                playSound={playSound}
              />
            )}

            {currentScreen === 'lobby' && (
              <LobbyScreen
                roomCode={currentRoom}
                currentUser={currentUser}
                onGameStart={handleGameStart}
                onLeaveLobby={handleLeaveLobby}
                isMuted={isMuted}
                toggleMute={toggleMute}
                playSound={playSound}
              />
            )}

            {currentScreen === 'game' && (
              <GameScreen
                roomCode={currentRoom}
                currentUser={currentUser}
                onGameEnd={handleGameEnd}
                isMuted={isMuted}
                toggleMute={toggleMute}
                playSound={playSound}
              />
            )}
          </>
        } />
      </Routes>
    </div>
  );
}

export default App;